import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WebTourContextValue {
  isTourRunning: boolean
  tourStepIndex: number
  startTour: () => void
  stopTour: () => void
  setTourStepIndex: (index: number) => void
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const WebTourContext = createContext<WebTourContextValue | undefined>(undefined)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const WebTourProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isTourRunning, setIsTourRunning] = useState(false)
  const [tourStepIndex, setTourStepIndex] = useState(0)

  const startTour = useCallback(() => {
    setTourStepIndex(0)
    setIsTourRunning(true)
  }, [])

  const stopTour = useCallback(() => {
    setIsTourRunning(false)
    setTourStepIndex(0)
  }, [])

  return (
    <WebTourContext.Provider
      value={{
        isTourRunning,
        tourStepIndex,
        startTour,
        stopTour,
        setTourStepIndex,
      }}
    >
      {children}
    </WebTourContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useWebTour = (): WebTourContextValue => {
  const ctx = useContext(WebTourContext)
  if (!ctx) {
    throw new Error('useWebTour must be used inside <WebTourProvider>')
  }
  return ctx
}
